import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { useCategories } from "@/hooks/useCategories";
import { useTransactions, useTransactionTypes } from "@/hooks/useTransactions";
import { formatDate, formatSignedCurrency } from "@/utils/formatters";

type EntityType = "account" | "tag" | "budget";

interface EntityTransactionsModalProps {
  entityType: EntityType;
  entityId: number | null;
  entityName: string;
  periodBalance: number;
  start?: string;
  end?: string;
  onClose: () => void;
}

export const EntityTransactionsModal = ({
  entityType,
  entityId,
  entityName,
  periodBalance,
  start,
  end,
  onClose,
}: EntityTransactionsModalProps) => {
  const { t } = useTranslation(["common", "accounts", "budget", "tags"]);
  const isAccount = entityType === "account";
  const namespace = isAccount ? "accounts" : entityType === "tag" ? "tags" : "budget";
  const label = useCallback(
    (key: string, defaultValue: string) => t(`${namespace}.${key}`, { defaultValue }),
    [namespace, t],
  );
  const [viewMode, setViewMode] = useState<"transactions" | "categories">("transactions");
  const { data: categories = [] } = useCategories();
  const { data: transactionTypes = [] } = useTransactionTypes();
  const categoryNameById = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);
  const transactionTypeById = useMemo(() => new Map(transactionTypes.map((type) => [type.id, type.name.toUpperCase()])), [transactionTypes]);
  const { data: transactions = [], isLoading, isError } = useTransactions(
    entityId === null
      ? undefined
      : {
          ...(isAccount ? { account_id_eq: entityId } : {}),
          ...(start && end ? { billable_date_gte: start, billable_date_lte: end } : {}),
          transaction_date: "desc",
        },
  );
  const entityTransactions = useMemo(
    () =>
      isAccount
        ? transactions
        : transactions.filter((transaction) =>
            entityType === "tag" ? transaction.tag_id === entityId : transaction.category_id === entityId,
          ),
    [entityId, entityType, isAccount, transactions],
  );
  const categoryTotals = useMemo(() => {
    const totals = new Map<string, { total: number; count: number }>();

    entityTransactions.forEach((transaction) => {
      const categoryName = transaction.category_id == null ? undefined : categoryNameById.get(transaction.category_id);
      const name = categoryName ?? label("uncategorized", "Sin categoría");
      const current = totals.get(name) ?? { total: 0, count: 0 };
      const amount = Math.abs(Number(transaction.amount ?? 0));
      const transactionType = transactionTypeById.get(transaction.type_id);
      const signedAmount = transactionType === "EXPENSE"
        ? -amount
        : transactionType === "DEPOSIT"
          ? amount
          : Number(transaction.amount ?? 0);

      totals.set(name, { total: current.total + signedAmount, count: current.count + 1 });
    });

    return [...totals.entries()]
      .map(([name, total]) => ({ name, ...total }))
      .sort((a, b) => b.total - a.total);
  }, [categoryNameById, entityTransactions, label, transactionTypeById]);

  if (entityId === null) return null;

  const subtitle = [
    start && end ? `${formatDate(start)} — ${formatDate(end)}` : null,
    `${label("periodBalance", "Balance del periodo")}: ${formatSignedCurrency(periodBalance)}`,
  ].filter(Boolean).join(" · ");

  return (
    <ModalShell
      isOpen={entityId !== null}
      onClose={onClose}
      title={entityName}
      subtitle={subtitle}
      labelledBy="entity-transactions-title"
      mobileSheet
      maxWidthClassName="max-w-2xl"
      contentClassName="px-5 py-2 sm:px-7"
      panelClassName="max-h-[min(calc(100dvh-1rem),760px)]"
      headerMeta={(
        <SegmentedControl
          value={viewMode}
          onChange={setViewMode}
          options={[
            { id: "transactions", label: label("transactionsList", "Movimientos") },
            { id: "categories", label: label("transactionsByCategory", "Por categoría") },
          ]}
        />
      )}
    >
          {isLoading ? <p className="py-10 text-center text-sm text-[var(--app-text-secondary)]">{t("common.status.loading", { defaultValue: "Cargando…" })}</p> : null}
          {isError ? <p className="py-10 text-center text-sm text-[var(--app-danger)]">{label("transactionsLoadError", "No se pudieron cargar movimientos.")}</p> : null}
          {!isLoading && !isError && entityTransactions.length === 0 ? (
            <p className="py-10 text-center text-sm text-[var(--app-text-secondary)]">
              {label("noTransactionsInPeriod", "No hay movimientos en este periodo.")}
            </p>
          ) : null}
          {viewMode === "categories"
            ? categoryTotals.map((category) => (
                <article key={category.name} className="flex items-center justify-between gap-4 border-b border-[color:color-mix(in_srgb,var(--app-border)_64%,transparent)] py-4 last:border-b-0">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-[var(--app-text)]">{category.name}</p>
                    <p className="mt-1 text-xs text-[var(--app-text-secondary)]">
                      {label("transactionsCount", "{{count}} movimientos").replace("{{count}}", String(category.count))}
                    </p>
                  </div>
                  <p className={`shrink-0 font-semibold tabular-nums ${category.total < 0 ? "text-[var(--app-danger)]" : category.total > 0 ? "text-[var(--app-success)]" : "text-[var(--app-text)]"}`}>
                    {formatSignedCurrency(category.total)}
                  </p>
                </article>
              ))
            : entityTransactions.map((transaction) => {
            const categoryName = transaction.category_id == null ? undefined : categoryNameById.get(transaction.category_id);
            const transactionType = transactionTypeById.get(transaction.type_id);
            const amount = Math.abs(Number(transaction.amount ?? 0));
            const signedAmount = transactionType === "EXPENSE"
              ? -amount
              : transactionType === "DEPOSIT"
                ? amount
                : Number(transaction.amount ?? 0);

            return (
              <article key={transaction.id} className="flex items-center justify-between gap-4 border-b border-[color:color-mix(in_srgb,var(--app-border)_64%,transparent)] py-4 last:border-b-0">
                <div className="min-w-0">
                  <p className="truncate font-medium text-[var(--app-text)]">{transaction.name}</p>
                  <p className="mt-1 text-xs text-[var(--app-text-secondary)]">
                    {formatDate(transaction.transaction_date)}
                    {categoryName ? <span> · {categoryName}</span> : null}
                  </p>
                </div>
                <p className={`shrink-0 font-semibold tabular-nums ${signedAmount < 0 ? "text-[var(--app-danger)]" : signedAmount > 0 ? "text-[var(--app-success)]" : "text-[var(--app-text)]"}`}>
                  {formatSignedCurrency(signedAmount)}
                </p>
              </article>
            );
              })}
    </ModalShell>
  );
};

interface AccountTransactionsModalProps {
  accountId: number | null;
  accountName: string;
  periodBalance: number;
  start: string;
  end: string;
  onClose: () => void;
}

export const AccountTransactionsModal = ({ accountId, accountName, periodBalance, start, end, onClose }: AccountTransactionsModalProps) => (
  <EntityTransactionsModal
    entityType="account"
    entityId={accountId}
    entityName={accountName}
    periodBalance={periodBalance}
    start={start}
    end={end}
    onClose={onClose}
  />
);
